import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios, { AxiosRequestConfig } from "axios";
import {
    Alert,
    AlertTitle,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    ListItemText,
    MenuItem,
    TextField,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import FormHeader from "../../../Common/components/templates/formHeader";
import FormDirections from "../../../Common/components/templates/formDirections";
import PrimaryButton from "../../../Common/components/templates/buttons/primary";
import SecondaryButton from "../../../Common/components/templates/buttons/secondary";
import { globalConfig } from "../../../Common/configuration/config";
import { getLocale } from "../../../Common/localization/localizationHelper";

interface Passkey {
    id: string;
    name: string;
    createdAt: string;
    lastAuthAt: string | null;
}

const darkMode = '@media (prefers-color-scheme: dark)';

// Mirrors the dark-mode dropdown styling used for the phone country list in App.css.
const menuPaperSx = {
    borderRadius: '8px',
    [darkMode]: {
        backgroundColor: '#3A4142',
        border: '1px solid #555555',
        color: '#ffffff',
        '& .MuiMenuItem-root:hover': {
            backgroundColor: '#4A5152',
        },
        '& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover': {
            backgroundColor: 'rgba(147, 205, 253, 0.16)',
        },
        '& .MuiMenuItem-root.Mui-focusVisible': {
            backgroundColor: '#4A5152',
        },
        '& .MuiListItemText-secondary': {
            color: 'rgba(255, 255, 255, 0.7)',
        },
        '& .MuiCheckbox-root': {
            color: '#C1CACB',
        },
        '& .MuiCheckbox-root.Mui-checked': {
            color: '#93CDFD',
        },
    },
};

const dialogPaperSx = {
    borderRadius: '8px',
    [darkMode]: {
        backgroundColor: '#3A4142',
        border: '1px solid #555555',
        color: '#ffffff',
        '& .MuiDialogContentText-root': {
            color: 'rgba(255, 255, 255, 0.7)',
        },
    },
};

interface ApiResponse<T> {
    data: T;
    isValid: boolean;
    message: string;
    errorCode: string | null;
}

const ResetPasskeys = () => {
    const intl = useIntl();
    const history = useHistory();
    const { getAccessTokenSilently } = useAuth0();
    const config = globalConfig.get();

    const [passkeys, setPasskeys] = useState<Passkey[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const getAxiosOptions = useCallback(async (): Promise<AxiosRequestConfig> => {
        const token = await getAccessTokenSilently();
        const options: AxiosRequestConfig = {
            headers: { Authorization: `Bearer ${token}` },
        };
        const locale = getLocale();
        if (locale) {
            options.params = { ui_locales: locale };
        }
        return options;
    }, [getAccessTokenSilently]);

    const loadPasskeys = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const options = await getAxiosOptions();
            const response = await axios.get<ApiResponse<Passkey[]>>(
                `${config.ApiUrl}/api/v1/profile/passkeys`,
                options
            );
            if (!response.data.isValid) {
                throw new Error(response.data.message);
            }
            setPasskeys(response.data.data ?? []);
        } catch (err) {
            console.error("Error loading passkeys:", err);
            setError(intl.formatMessage({ id: "passkey.reset.error.load", defaultMessage: "Unable to load your passkeys." }));
        } finally {
            setLoading(false);
        }
    }, [config.ApiUrl, getAxiosOptions, intl]);

    useEffect(() => {
        loadPasskeys();
    }, [loadPasskeys]);

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as string | string[];
        setSelectedIds(typeof value === "string" ? value.split(",") : value);
        setSuccess(false);
    };

    const handleDelete = async () => {
        setConfirmOpen(false);
        if (selectedIds.length === 0) return;
        setDeleting(true);
        setError(null);
        setSuccess(false);
        try {
            const options = await getAxiosOptions();
            const response = await axios.post<ApiResponse<unknown>>(
                `${config.ApiUrl}/api/v1/profile/passkeys/delete`,
                selectedIds,
                options
            );
            if (response.data && response.data.isValid === false) {
                throw new Error(response.data.message);
            }
            setSelectedIds([]);
            setSuccess(true);
            await loadPasskeys();
        } catch (err) {
            console.error("Error deleting passkeys:", err);
            setError(intl.formatMessage({ id: "passkey.reset.error.delete", defaultMessage: "Unable to remove the selected passkeys." }));
        } finally {
            setDeleting(false);
        }
    };

    const nameById = (id: string) => passkeys.find(p => p.id === id)?.name ?? id;

    const formatDate = (value: string | null) =>
        value ? new Date(value).toLocaleString(intl.locale) : intl.formatMessage({ id: "passkey.reset.never", defaultMessage: "Never" });

    const label = intl.formatMessage({ id: "passkey.reset.select.label", defaultMessage: "Passkeys" });
    const placeholder = intl.formatMessage({ id: "passkey.reset.select.placeholder", defaultMessage: "Select passkeys..." });

    return (
        <form className="form" onSubmit={e => e.preventDefault()}>
            <FormHeader title={intl.formatMessage({ id: "passkey.reset.header", defaultMessage: "Remove Passkeys" })} />
            <div className="content">
                <FormDirections>
                    <FormattedMessage
                        id="passkey.reset.description"
                        defaultMessage="Select the passkeys you want to remove from your account."
                    />
                </FormDirections>

                {error && <Alert severity="error">{error}</Alert>}
                {success && (
                    <Alert severity="success">
                        <AlertTitle>
                            <FormattedMessage id="passkey.reset.success.title" defaultMessage="Passkey(s) Removed" />
                        </AlertTitle>
                        <p>
                            <FormattedMessage id="passkey.reset.success" defaultMessage="The selected passkey(s) have been removed from your account." />
                        </p>
                        <p>
                            <FormattedMessage
                                id="passkey.reset.success.local"
                                defaultMessage="Any corresponding passkeys stored on your devices or in your password manager will remain on those devices until you remove them manually."
                            />
                        </p>
                        <p>
                            <FormattedMessage
                                id="passkey.reset.success.signInFails"
                                defaultMessage="If you attempt to use one of those locally stored passkeys to sign in, authentication will fail because the passkey is no longer registered with your account."
                            />
                        </p>
                        <p>
                            <FormattedMessage
                                id="passkey.reset.success.registerNew"
                                defaultMessage="You can register a new passkey at any time."
                            />
                        </p>
                    </Alert>
                )}

                {loading ? (
                    <CircularProgress />
                ) : passkeys.length === 0 ? (
                    <FormDirections>
                        <FormattedMessage id="passkey.reset.empty" defaultMessage="You don't have any passkeys registered." />
                    </FormDirections>
                ) : (
                    <>
                        <label htmlFor="passkeys-select" className="label">{label}</label>
                        <TextField
                            select
                            fullWidth
                            id="passkeys-select"
                            variant="outlined"
                            value={selectedIds}
                            onChange={handleChange}
                            disabled={deleting}
                            SelectProps={{
                                multiple: true,
                                displayEmpty: true,
                                renderValue: selected => {
                                    const ids = selected as string[];
                                    return ids.length === 0
                                        ? <span style={{ opacity: 0.42 }}>{placeholder}</span>
                                        : ids.map(nameById).join(", ");
                                },
                                MenuProps: { PaperProps: { sx: menuPaperSx } },
                                inputProps: { "aria-label": label },
                            }}
                        >
                            {passkeys.map(passkey => (
                                <MenuItem key={passkey.id} value={passkey.id}>
                                    <Checkbox checked={selectedIds.includes(passkey.id)} />
                                    <ListItemText
                                        primary={passkey.name}
                                        secondary={
                                            <>
                                                <FormattedMessage id="passkey.reset.createdAt" defaultMessage="Created" />
                                                {`: ${formatDate(passkey.createdAt)} | `}
                                                <FormattedMessage id="passkey.reset.lastUsed" defaultMessage="Last used" />
                                                {`: ${formatDate(passkey.lastAuthAt)}`}
                                            </>
                                        }
                                    />
                                </MenuItem>
                            ))}
                        </TextField>
                    </>
                )}

                <PrimaryButton
                    variant="contained"
                    color="primary"
                    onClick={() => setConfirmOpen(true)}
                    disabled={loading || deleting || selectedIds.length === 0}
                >
                    <FormattedMessage id="passkey.reset.button.delete" defaultMessage="Remove selected passkeys" />
                </PrimaryButton>
                <SecondaryButton onClick={() => history.push("/add-passkey")} disabled={deleting}>
                    <FormattedMessage id="passkey.reset.link.add" defaultMessage="Add a passkey" />
                </SecondaryButton>
            </div>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                fullWidth
                aria-labelledby="passkeys-confirm-title"
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle id="passkeys-confirm-title">
                    <FormattedMessage id="passkey.reset.confirm.title" defaultMessage="Remove Passkeys" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText paragraph>
                        <FormattedMessage
                            id="passkey.reset.confirm.intro"
                            defaultMessage="You are about to remove the selected passkey(s) from your account."
                        />
                    </DialogContentText>
                    <DialogContentText paragraph>
                        <FormattedMessage
                            id="passkey.reset.confirm.scope"
                            defaultMessage="Removing a passkey here will prevent it from being used to sign in to your account. This action only removes the passkey registration stored for your account and does not remove any passkeys stored on your devices or in your password manager."
                        />
                    </DialogContentText>
                    <DialogContentText paragraph>
                        <FormattedMessage
                            id="passkey.reset.confirm.cleanup"
                            defaultMessage="If you no longer intend to use these passkeys, you should also remove them from your device(s) or password manager."
                        />
                    </DialogContentText>
                    <DialogContentText>
                        <FormattedMessage
                            id="passkey.reset.confirm.registerNew"
                            defaultMessage="After removal, you can register a new passkey at any time."
                        />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <SecondaryButton onClick={() => setConfirmOpen(false)}>
                        <FormattedMessage id="passkey.reset.confirm.cancel" defaultMessage="Cancel" />
                    </SecondaryButton>
                    <PrimaryButton variant="contained" color="primary" onClick={handleDelete}>
                        <FormattedMessage id="passkey.reset.confirm.remove" defaultMessage="Remove" />
                    </PrimaryButton>
                </DialogActions>
            </Dialog>
        </form>
    );
};

export default ResetPasskeys;
